// Which `derive` fill rules the OS can actually work out.
//
// CONSUMER: shared (the only-what-you-know screen, and whatever
// computes a derived blank once one is implemented).
//
// ── WHAT A DERIVE RULE PROMISES ─────────────────────────────────────
//
// The atlas records one fill rule per blank. `derive` is the one that
// says "a named calculation over OS data fills this — nobody types it":
// the counter offer's own number (prior counters + 1), which party is
// countering (the record's side), which addenda a contract incorporates
// (the forms in this package).
//
// That is a PROMISE made to the sender, and only-what-you-know keeps it
// by leaving those blanks off the list of questions and counting them
// among the ones "the OS filled for you."
//
// ── WHY THIS FILE EXISTS ────────────────────────────────────────────
//
// Measured 2026-09-16, before writing a line of this: the Realty atlas
// carries 87 `derive` rows, 68 of which survive into built layouts, and
// NOT ONE derive rule is implemented anywhere in the codebase. Every one
// of those 68 blanks went out empty, was never on the sender's question
// list, and was counted in the "the OS filled N" number the screen leads
// with. Among them: the counter offer's own number, which document it
// counters, whether the person countering is the buyer or the seller,
// and all twelve "Addenda Incorporated" boxes on the purchase contract.
//
// A contract that understates its own incorporated addenda, and a screen
// claiming credit for filling it, is the accuracy rule in CLAUDE.md
// failing quietly: a number the team sees is a promise.
//
// So the promise is now checkable. A derive rule counts as kept only
// when it is named here, beside code that computes it. Everything else
// is asked, exactly like a `gap` — because from the sender's chair an
// unimplemented calculation and a blank with no home are the same thing:
// nobody filled it and nobody told you.
//
// ── ADDING ONE ──────────────────────────────────────────────────────
//
// Land the entry in the SAME change as the code that computes the
// value. An entry here with no implementation behind it re-creates the
// exact defect this file was written to end, and does it invisibly,
// because the blank goes straight back to being counted as filled.

import { addendumFormKeyForRule } from "./addenda-boxes";
import type { EsignFieldType } from "./field-types";
import { OFFER_DERIVE_RULE_NAMES, offerDeriveRuleFor } from "./offer-derive-rules";

/** The machine-readable name of a derive rule, in either shape it comes
 *  in.
 *
 *  A rule written by hand carries the name and nothing else, which is
 *  the canonical form fill-rules.ts documents:
 *
 *      derive:offer.response_deadline
 *
 *  A rule written by an atlas reviewer says it out loud and puts the
 *  name in parentheses, because the sentence is what a human reviews:
 *
 *      derive:rule: checked only when the H.O.A. addendum is in this
 *                   package (offer.package.forms)
 *
 *  Both are read here. 53 of the atlas's 87 rows name a token one way or
 *  the other; the remaining 34 describe the calculation in prose alone
 *  ("number of prior counter offers on the transaction + 1") and have no
 *  name at all.
 *
 *  A rule with no name can never be registered, and that is the right
 *  outcome rather than a shortcoming: a calculation nobody can name is
 *  one nobody can look up, so it must be asked. */
export function deriveRuleName(ref: string | null | undefined): string | null {
  const trimmed = ref?.trim();
  if (!trimmed) return null;
  // The whole ref is already just the name.
  if (/^[a-z_]+(?:\.[a-z_]+)+$/.test(trimmed)) return trimmed;
  // Otherwise it is prose that may carry the name in parentheses.
  const m = /\(([a-z_]+(?:\.[a-z_]+)+)\)/.exec(trimmed);
  return m ? (m[1] ?? null) : null;
}

/**
 * The rules with code behind them.
 *
 * Every name here is implemented in shared/esign/offer-derive-rules.ts,
 * which is the one table of "worked out for you" boxes and the code that
 * works each out (offer-writing program, session 4, 2026-11-04). It
 * covers the offer's parts (close of escrow month / day / year, the
 * financing boxes, concessions as a percent or dollars, possession, the
 * inspection days, the warranty, the personal property, the expiry, the
 * earnest money's form and holder, lines 12 to 16), the house (sewer or
 * septic, solar), the package (the Addenda Incorporated boxes) and the
 * side of the deal (the SCA's originator, each signer's party, the agency
 * election and confirmation).
 *
 * Still empty-handed: the counter offer's own number and which document
 * it counters (prose rules with no name; the counter-offer program).
 *
 * `offer.package.forms` is CONDITIONAL — see deriveRuleIsImplemented.
 */
export const IMPLEMENTED_DERIVE_RULES: ReadonlySet<string> = new Set<string>(OFFER_DERIVE_RULE_NAMES);

/** What a caller knows about the box besides its rule, when it knows
 *  it. Most offer rules are told apart by the rule's words alone; a few
 *  (the financing boxes, lines 12 to 16, the agency confirmation) are
 *  told apart by the caption printed beside the box. */
export interface DeriveRuleBox {
  caption?: string | null;
  fieldType?: EsignFieldType | null;
  roleKey?: string | null;
}

/** True when this derive rule has code behind it, so the blank really
 *  does fill itself and the sender is right not to be asked.
 *
 *  `ref` is the whole rule as the atlas wrote it, not just the name,
 *  because one rule name can cover blanks the code can answer and
 *  blanks it cannot. `offer.package.forms` is the first: the same
 *  sentence shape names twelve different addenda, eight of which the
 *  form catalog knows and four of which it does not. A rule that is
 *  registered but cannot answer THIS blank is not implemented for this
 *  blank, and the sender is asked.
 *
 *  `box` lets a caller that holds the field pass its caption and type;
 *  without it, a rule told apart by caption alone cannot be recognised
 *  and is reported as unimplemented, which is the honest side to err on. */
export function deriveRuleIsImplemented(ref: string | null | undefined, box?: DeriveRuleBox): boolean {
  const text = ref?.trim() ?? "";
  // A rule with no words and no box to look at names nothing.
  if (!text && !box?.caption?.trim()) return false;
  const name = deriveRuleName(ref);
  if (name === "offer.package.forms") return addendumFormKeyForRule(ref) !== null;
  const types: EsignFieldType[] = box?.fieldType ? [box.fieldType] : ["checkbox", "text"];
  for (const fieldType of types) {
    const rule = offerDeriveRuleFor({
      ref: text,
      caption: box?.caption ?? "",
      fieldType,
      roleKey: box?.roleKey ?? null,
    });
    if (rule) return true;
  }
  return false;
}
